// src/components/SearchModal.js
import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useNotes } from './contexts/NotesContext';
import styles from './SearchModal.module.css';

const SearchModal = ({ isOpen, onClose, onNoteSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { notes } = useNotes();
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear results when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Handle search when term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Simple search implementation - can be enhanced for better results
    const results = notes.filter(note => {
      const titleMatch = note.title && note.title.toLowerCase().includes(searchTerm.toLowerCase());
      const contentMatch = note.note && note.note.toLowerCase().includes(searchTerm.toLowerCase());
      return titleMatch || contentMatch;
    });

    setSearchResults(results);
    setIsSearching(false);
  }, [searchTerm, notes]);

  const handleNoteClick = (noteId) => {
    onNoteSelect(noteId);
    onClose();
  };

  // Handle key presses
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div 
        className={styles.modalContent} 
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.modalHeader}>
          <div className={styles.searchInputContainer}>
            <Search className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search in notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={styles.resultsContainer}>
          {isSearching ? (
            <div className={styles.searching}>Searching...</div>
          ) : searchTerm && searchResults.length === 0 ? (
            <div className={styles.noResults}>No results found</div>
          ) : (
            <div className={styles.resultsList}>
              {searchResults.map(note => (
                <div 
                  key={note.id} 
                  className={styles.resultItem}
                  onClick={() => handleNoteClick(note.id)}
                >
                  <h3 className={styles.resultTitle}>{note.title || 'Untitled'}</h3>
                  {note.note && (
                    <p className={styles.resultPreview}>
                      {stripHtmlTags(note.note).substring(0, 100)}
                      {stripHtmlTags(note.note).length > 100 ? '...' : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to strip HTML tags for preview
function stripHtmlTags(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export default SearchModal;